import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  Calendar,
  Clock,
  FileText,
  User2,
  Briefcase,
  ChevronRight,
  Trash2,
  X,
  Image as ImageIcon,
} from "lucide-react";
import {
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import {
  LocalizationProvider,
  DateTimePicker,
  DatePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { materials, designs } from "./dummyProductionData";

// Sample data for dropdowns
const karigars = [
  { id: 1, name: "John Doe", specialization: "Cutting" },
  { id: 2, name: "Jane Smith", specialization: "Stitching" },
  { id: 3, name: "Mike Wilson", specialization: "Finishing" },
];

const jobTypes = [
  { id: 1, name: "Cutting" },
  { id: 2, name: "Stitching" },
  { id: 3, name: "Finishing" },
];

const TransferJobslip = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const jobslipId = searchParams.get("id");

  const [form, setForm] = useState({
    transferredBy: "Mike Wilson", // Non-editable
    transferredTo: "",
    jobType: "",
    jobRate: "",
    transferDateTime: dayjs(),
    challanNo: "",
    expectedDate: dayjs(),
    notes: "",
  });

  const [errors, setErrors] = useState({});
  const [isMaterialSectionCollapsed, setIsMaterialSectionCollapsed] =
    useState(true);
  const [transferQuantities, setTransferQuantities] = useState({});
  const [showTakaModal, setShowTakaModal] = useState(false);
  const [selectedStockEntry, setSelectedStockEntry] = useState(null);
  const [newTakaQuantity, setNewTakaQuantity] = useState("");
  const [newTakaValue, setNewTakaValue] = useState("");
  const [takaInputFields, setTakaInputFields] = useState({});
  const [itemNotes, setItemNotes] = useState({});
  const [itemImages, setItemImages] = useState({});
  const [transferAll, setTransferAll] = useState(false);
  const [materialPaymentDue, setMaterialPaymentDue] = useState(false);
  const [isDesignSectionCollapsed, setIsDesignSectionCollapsed] =
    useState(true);
  const [transferDesignQuantities, setTransferDesignQuantities] = useState({});
  const [transferAllDesigns, setTransferAllDesigns] = useState(false);
  const [designPaymentDue, setDesignPaymentDue] = useState(false);
  const [transferMode, setTransferMode] = useState("overall");
  const [designTransferMode, setDesignTransferMode] = useState("overall");

  // Using only first two materials
  const transferMaterials = materials.slice(0, 2);

  // Using only first two designs
  const transferDesigns = designs.slice(0, 2);

  const handleChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleRemoveItem = id => {
    // setSelectedMaterials(prev => prev.filter(item => item.id !== id)); // This line was removed
    delete itemNotes[id];
    delete itemImages[id];
  };

  const handleAddNote = id => {
    const note = prompt("Enter note for the material:");
    if (note) {
      setItemNotes(prev => ({
        ...prev,
        [id]: note,
      }));
    }
  };

  const handleImageUpload = (id, event) => {
    const files = Array.from(event.target.files);
    const imageUrls = files.map(file => URL.createObjectURL(file));

    setItemImages(prev => ({
      ...prev,
      [id]: [...(prev[id] || []), ...imageUrls],
    }));
  };

  const handleRemoveImage = (itemId, imageIndex) => {
    setItemImages(prev => ({
      ...prev,
      [itemId]: prev[itemId].filter((_, index) => index !== imageIndex),
    }));
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleTransferQuantityChange = (materialId, color, value) => {
    setTransferQuantities(prev => ({
      ...prev,
      [materialId]: {
        ...prev[materialId],
        [color]: value,
      },
    }));
  };

  const handleAddTakaClick = (materialId, color, unit, quantity) => {
    setSelectedStockEntry({
      id: `${materialId}-${color}`,
      color,
      unit,
      quantity,
    });
    setShowTakaModal(true);
    setNewTakaQuantity("");
    setNewTakaValue("");
  };

  const handleCloseTakaModal = () => {
    setShowTakaModal(false);
    setSelectedStockEntry(null);
    setNewTakaQuantity("");
    setNewTakaValue("");
  };

  const handleAddTakaDenomination = () => {
    if (!newTakaQuantity || !newTakaValue) return;

    const quantity = parseFloat(newTakaQuantity);
    const value = parseFloat(newTakaValue);

    if (quantity <= 0 || value <= 0) return;

    const entryId = selectedStockEntry.id;
    const currentFields = takaInputFields[entryId] || [];

    setTakaInputFields(prev => ({
      ...prev,
      [entryId]: [
        ...currentFields,
        {
          quantity: newTakaQuantity,
          value: newTakaValue,
          id: Date.now(),
        },
      ],
    }));

    setNewTakaQuantity("");
    setNewTakaValue("");
  };

  const handleRemoveTakaField = (entryId, fieldId) => {
    setTakaInputFields(prev => ({
      ...prev,
      [entryId]: prev[entryId]?.filter(field => field.id !== fieldId) || [],
    }));
  };

  const shouldShowTakaButton = unit => {
    return ["meters", "kg", "yards"].includes(unit);
  };

  // Add handler for transfer all checkbox
  const handleTransferAllChange = event => {
    setTransferAll(event.target.checked);
    if (event.target.checked) {
      // Create an object to store all quantities
      const allQuantities = {};
      transferMaterials.forEach(material => {
        allQuantities[material.id] = {};
        material.colors.split(", ").forEach(color => {
          allQuantities[material.id][color] = material.inStock;
        });
      });
      setTransferQuantities(allQuantities);
    } else {
      // Reset all quantities to 0
      const resetQuantities = {};
      transferMaterials.forEach(material => {
        resetQuantities[material.id] = {};
        material.colors.split(", ").forEach(color => {
          resetQuantities[material.id][color] = 0;
        });
      });
      setTransferQuantities(resetQuantities);
    }
  };

  const handleTransferDesignQuantityChange = (
    designId,
    pieceType,
    color,
    size,
    value,
  ) => {
    setTransferDesignQuantities(prev => ({
      ...prev,
      [designId]: {
        ...prev[designId],
        [pieceType]: {
          ...prev[designId]?.[pieceType],
          [color]: {
            ...prev[designId]?.[pieceType]?.[color],
            [size]: value,
          },
        },
      },
    }));
  };

  const handleTransferAllDesignsChange = event => {
    setTransferAllDesigns(event.target.checked);
    if (event.target.checked) {
      // Set all quantities to maximum
      const allQuantities = {};
      transferDesigns.forEach(design => {
        allQuantities[design.id] = {};
        design.pieces?.forEach(piece => {
          allQuantities[design.id][piece.pieceType] = {};
          piece.colors?.forEach(color => {
            allQuantities[design.id][piece.pieceType][color.name] = {};
            Object.entries(color.sizes || {}).forEach(([size, qty]) => {
              allQuantities[design.id][piece.pieceType][color.name][size] = qty;
            });
          });
        });
      });
      setTransferDesignQuantities(allQuantities);
    } else {
      // Reset all quantities to 0
      setTransferDesignQuantities({});
    }
  };

  return (
    <div className="bg-white h-full">
      {/* Header Section */}
      <div className="flex items-center justify-between px-6 py-4 border-b">
        <div className="flex items-center gap-4">
          <button
            onClick={handleBack}
            className="p-1.5 rounded-md bg-gray-50 hover:bg-gray-100 transition-colors">
            <ArrowLeft size={20} className="text-gray-600" />
          </button>
          <h1 className="text-2xl font-semibold font-outfit text-gray-800">
            Transfer Job Slip {jobslipId ? `#${jobslipId}` : ""}
          </h1>
        </div>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 gap-6">
          {/* Basic Information */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-base font-medium text-gray-800 mb-4">
              Basic Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Transferred By - Non-editable */}
              <TextField
                label="Transferred By"
                size="small"
                value={form.transferredBy}
                disabled
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <User2 size={18} className="text-[#5652B0]" />
                    </InputAdornment>
                  ),
                }}
              />

              {/* Transferred To */}
              <FormControl
                fullWidth
                size="small"
                error={Boolean(errors.transferredTo)}
                required>
                <InputLabel>Transferred To</InputLabel>
                <Select
                  value={form.transferredTo}
                  label="Transferred To"
                  onChange={e => handleChange("transferredTo", e.target.value)}
                  startAdornment={
                    <InputAdornment position="start">
                      <User2 size={18} className="text-[#5652B0]" />
                    </InputAdornment>
                  }>
                  {karigars.map(karigar => (
                    <MenuItem key={karigar.id} value={karigar.id}>
                      {karigar.name} - {karigar.specialization}
                    </MenuItem>
                  ))}
                </Select>
                {errors.transferredTo && (
                  <span className="text-xs text-red-500 mt-1">
                    {errors.transferredTo}
                  </span>
                )}
              </FormControl>

              {/* Job Type */}
              <FormControl
                fullWidth
                size="small"
                error={Boolean(errors.jobType)}
                required>
                <InputLabel>Select Job Type</InputLabel>
                <Select
                  value={form.jobType}
                  label="Select Job Type"
                  onChange={e => handleChange("jobType", e.target.value)}
                  startAdornment={
                    <InputAdornment position="start">
                      <Briefcase size={18} className="text-[#5652B0]" />
                    </InputAdornment>
                  }>
                  {jobTypes.map(type => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.jobType && (
                  <span className="text-xs text-red-500 mt-1">
                    {errors.jobType}
                  </span>
                )}
              </FormControl>

              {/* Job Rate */}
              <TextField
                label="Job Rate"
                size="small"
                required
                type="number"
                value={form.jobRate}
                onChange={e => handleChange("jobRate", e.target.value)}
                error={Boolean(errors.jobRate)}
                helperText={errors.jobRate}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">₹</InputAdornment>
                  ),
                }}
              />

              {/* Transfer Date Time */}
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  label="Date and Time of Transfer"
                  value={form.transferDateTime}
                  onChange={newValue =>
                    handleChange("transferDateTime", newValue)
                  }
                  slotProps={{
                    textField: {
                      size: "small",
                      required: true,
                      error: Boolean(errors.transferDateTime),
                      helperText: errors.transferDateTime,
                      InputProps: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Clock size={18} className="text-[#5652B0]" />
                          </InputAdornment>
                        ),
                      },
                    },
                  }}
                />
              </LocalizationProvider>

              {/* Challan No */}
              <TextField
                label="Lot No/Transfer Challan No."
                size="small"
                required
                value={form.challanNo}
                onChange={e => handleChange("challanNo", e.target.value)}
                error={Boolean(errors.challanNo)}
                helperText={errors.challanNo}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FileText size={18} className="text-[#5652B0]" />
                    </InputAdornment>
                  ),
                }}
              />

              {/* Expected Date */}
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Expected Date"
                  value={form.expectedDate}
                  onChange={newValue => handleChange("expectedDate", newValue)}
                  slotProps={{
                    textField: {
                      size: "small",
                      required: true,
                      error: Boolean(errors.expectedDate),
                      helperText: errors.expectedDate,
                      InputProps: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Calendar size={18} className="text-[#5652B0]" />
                          </InputAdornment>
                        ),
                      },
                    },
                  }}
                />
              </LocalizationProvider>

              <TextField
                label="Notes"
                multiline
                rows={1}
                fullWidth
                size="small"
                value={form.notes}
                onChange={e => handleChange("notes", e.target.value)}
                placeholder="Add any additional notes here..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FileText size={18} className="text-[#5652B0]" />
                    </InputAdornment>
                  ),
                }}
                className="md:col-span-3"
              />
            </div>
          </div>

          {/* Transfer Material Section */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div
              className="flex items-center justify-between cursor-pointer"
              onClick={() => setIsMaterialSectionCollapsed(prev => !prev)}>
              <div className="flex items-center gap-1.5">
                <ChevronRight
                  size={16}
                  className={`text-gray-500 transition-transform ${
                    !isMaterialSectionCollapsed ? "rotate-90" : ""
                  }`}
                />
                <div className="flex items-center gap-4">
                  <h2 className="text-base font-medium text-gray-800">
                    Transfer Materials
                  </h2>
                  {!isMaterialSectionCollapsed && (
                    <FormControl
                      size="small"
                      sx={{
                        minWidth: 180,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          backgroundColor: "#f8fafc",
                          "&:hover": {
                            backgroundColor: "#f1f5f9",
                          },
                          "& fieldset": {
                            borderColor: "#e2e8f0",
                          },
                          "&:hover fieldset": {
                            borderColor: "#94a3b8",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#5652B0",
                          },
                        },
                      }}>
                      <Select
                        value={transferMode}
                        onChange={e => setTransferMode(e.target.value)}
                        displayEmpty
                        className="text-sm font-medium"
                        onClick={e => e.stopPropagation()}
                        sx={{
                          "& .MuiSelect-select": {
                            padding: "8px 14px",
                          },
                        }}>
                        <MenuItem
                          value="overall"
                          sx={{
                            borderRadius: "8px",
                            margin: "4px",
                            "&:hover": {
                              backgroundColor: "#f1f5f9",
                            },
                            "&.Mui-selected": {
                              backgroundColor: "#5652B0 !important",
                              color: "white",
                              "&:hover": {
                                backgroundColor: "#4A47A3 !important",
                              },
                            },
                          }}>
                          Overall Transfer
                        </MenuItem>
                        <MenuItem
                          value="colour_wise"
                          sx={{
                            borderRadius: "8px",
                            margin: "4px",
                            "&:hover": {
                              backgroundColor: "#f1f5f9",
                            },
                            "&.Mui-selected": {
                              backgroundColor: "#5652B0 !important",
                              color: "white",
                              "&:hover": {
                                backgroundColor: "#4A47A3 !important",
                              },
                            },
                          }}>
                          Colour Wise Transfer
                        </MenuItem>
                      </Select>
                    </FormControl>
                  )}
                </div>
                {isMaterialSectionCollapsed && (
                  <span className="text-sm text-gray-500">
                    {transferMaterials.length > 0 ? (
                      <>
                        :{" "}
                        {transferMaterials.map(
                          (item, index) =>
                            item.name +
                            (index < transferMaterials.length - 1 ? ", " : ""),
                        )}
                      </>
                    ) : (
                      ": No materials added yet"
                    )}
                  </span>
                )}
              </div>
              {!isMaterialSectionCollapsed && (
                <div className="flex items-center gap-4">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={transferAll}
                        onChange={handleTransferAllChange}
                        sx={{
                          color: "#5652B0",
                          "&.Mui-checked": {
                            color: "#5652B0",
                          },
                        }}
                      />
                    }
                    label={
                      <span className="text-sm font-medium text-gray-700">
                        Transfer All
                      </span>
                    }
                    onClick={e => e.stopPropagation()}
                  />
                  <div className="h-6 w-px bg-gray-200" />{" "}
                  {/* Vertical separator */}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={materialPaymentDue}
                        onChange={e => setMaterialPaymentDue(e.target.checked)}
                        sx={{
                          color: "#5652B0",
                          "&.Mui-checked": {
                            color: "#5652B0",
                          },
                        }}
                      />
                    }
                    label={
                      <span className="text-sm font-medium text-gray-700">
                        Payment Due
                      </span>
                    }
                    onClick={e => e.stopPropagation()}
                  />
                </div>
              )}
            </div>

            <div
              className={`space-y-4 ${
                isMaterialSectionCollapsed ? "hidden" : "mt-4"
              }`}>
              {transferMaterials.map(material => (
                <div
                  key={material.id}
                  className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="p-4 border-b border-gray-100 bg-gray-50/50">
                    <div className="flex items-start gap-4">
                      <img
                        src={material.image}
                        alt={material.name}
                        className="w-16 h-16 object-cover rounded-lg bg-gray-100"
                      />
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start flex-col">
                            <div className="flex items-center gap-4">
                              <h3 className="text-lg font-medium text-gray-800 ml-0">
                                {material.name}
                              </h3>
                              <span className="text-sm text-gray-600">
                                {transferQuantities[material.id]?.default || 0}/
                                {material.inStock} {material.unit}
                              </span>
                              <div className="flex items-center gap-2">
                                <TextField
                                  size="small"
                                  type="number"
                                  label="Transfer Quantity"
                                  className="min-w-[150px]"
                                  disabled={transferMode === "colour_wise"}
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "8px",
                                      backgroundColor:
                                        transferMode === "colour_wise"
                                          ? "#f3f4f6"
                                          : "transparent",
                                      "& fieldset": {
                                        borderColor:
                                          transferMode === "colour_wise"
                                            ? "#d1d5db"
                                            : "#e5e7eb",
                                      },
                                      "&:hover fieldset": {
                                        borderColor:
                                          transferMode === "colour_wise"
                                            ? "#d1d5db"
                                            : "#9ca3af",
                                      },
                                      "& input": {
                                        color:
                                          transferMode === "colour_wise"
                                            ? "#9ca3af"
                                            : "inherit",
                                      },
                                    },
                                    "& .MuiInputLabel-root": {
                                      color:
                                        transferMode === "colour_wise"
                                          ? "#9ca3af"
                                          : "#6b7280",
                                      fontWeight: 300,
                                    },
                                  }}
                                  value={
                                    transferQuantities[material.id]?.default ||
                                    ""
                                  }
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        {material.unit}
                                      </InputAdornment>
                                    ),
                                  }}
                                  onChange={e =>
                                    handleTransferQuantityChange(
                                      material.id,
                                      "default",
                                      e.target.value,
                                    )
                                  }
                                />
                                {shouldShowTakaButton(material.unit) && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleAddTakaClick(
                                        material.id,
                                        "default",
                                        material.unit,
                                        transferQuantities[material.id]
                                          ?.default || 0,
                                      )
                                    }
                                    className="px-3 py-1.5 text-xs font-medium text-[#5652B0] border border-[#5652B0] rounded-lg hover:bg-[#5652B0] hover:text-white transition-all">
                                    {takaInputFields[`${material.id}-default`]
                                      ?.length > 0
                                      ? "Edit Taka"
                                      : "Add Taka"}
                                  </button>
                                )}
                              </div>
                            </div>
                            <p className="text-sm text-gray-500 mt-0.5">
                              {material.unit}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="p-4">
                    <div className="space-y-4">
                      {transferMode === "colour_wise" &&
                        material.colors.split(", ").map(color => (
                          <div key={color} className="flex items-center gap-4">
                            <div className="min-w-[120px]">
                              <span className="text-sm font-medium text-gray-700">
                                {color}
                              </span>
                            </div>
                            <div className="min-w-[200px]">
                              <span className="text-sm text-gray-600">
                                {transferQuantities[material.id]?.[color] || 0}/
                                {material.inStock} {material.unit}
                              </span>
                            </div>
                            <div className="min-w-[150px]">
                              <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Transfer Quantity"
                                value={
                                  transferQuantities[material.id]?.[color] || ""
                                }
                                onChange={e =>
                                  handleTransferQuantityChange(
                                    material.id,
                                    color,
                                    e.target.value,
                                  )
                                }
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      {material.unit}
                                    </InputAdornment>
                                  ),
                                }}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "8px",
                                    "& fieldset": {
                                      borderColor: "#e5e7eb",
                                    },
                                    "&:hover fieldset": {
                                      borderColor: "#d1d5db",
                                    },
                                  },
                                  "& .MuiInputLabel-root": {
                                    color: "#9ca3af",
                                    fontWeight: 300,
                                  },
                                }}
                              />
                            </div>
                            {shouldShowTakaButton(material.unit) && (
                              <div className="min-w-[100px]">
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleAddTakaClick(
                                      material.id,
                                      color,
                                      material.unit,
                                      transferQuantities[material.id]?.[
                                        color
                                      ] || 0,
                                    )
                                  }
                                  className="w-full px-3 py-2 text-xs font-medium text-[#5652B0] border border-[#5652B0] rounded-lg hover:bg-[#5652B0] hover:text-white transition-all">
                                  {takaInputFields[`${material.id}-${color}`]
                                    ?.length > 0
                                    ? "Edit Taka"
                                    : "Add Taka"}
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  </div>

                  {/* Taka Display Section */}
                  {material.colors.split(", ").map(color => {
                    const entryId = `${material.id}-${color}`;
                    return takaInputFields[entryId]?.length > 0 ? (
                      <div
                        key={entryId}
                        className="px-4 py-3 border-t border-gray-100">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-600 font-medium">
                            {color} Taka:
                          </span>
                          <div className="flex flex-wrap gap-1">
                            {takaInputFields[entryId].map(field => (
                              <span
                                key={field.id}
                                className="px-2 py-1 bg-gray-100 text-xs font-medium rounded">
                                {field.quantity}x{field.value}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : null;
                  })}
                </div>
              ))}
            </div>
          </div>

          {/* Transfer Design Section */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div
              className="flex items-center justify-between cursor-pointer"
              onClick={() => setIsDesignSectionCollapsed(prev => !prev)}>
              <div className="flex items-center gap-1.5">
                <ChevronRight
                  size={16}
                  className={`text-gray-500 transition-transform ${
                    !isDesignSectionCollapsed ? "rotate-90" : ""
                  }`}
                />
                <div className="flex items-center gap-4">
                  <h2 className="text-base font-medium text-gray-800">
                    Transfer Designs
                  </h2>
                  {!isDesignSectionCollapsed && (
                    <FormControl
                      size="small"
                      sx={{
                        minWidth: 180,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          backgroundColor: "#f8fafc",
                          "&:hover": {
                            backgroundColor: "#f1f5f9",
                          },
                          "& fieldset": {
                            borderColor: "#e2e8f0",
                          },
                          "&:hover fieldset": {
                            borderColor: "#94a3b8",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#5652B0",
                          },
                        },
                      }}>
                      <Select
                        value={designTransferMode}
                        onChange={e => setDesignTransferMode(e.target.value)}
                        displayEmpty
                        className="text-sm font-medium"
                        onClick={e => e.stopPropagation()}
                        sx={{
                          "& .MuiSelect-select": {
                            padding: "8px 14px",
                          },
                        }}>
                        <MenuItem
                          value="overall"
                          sx={{
                            borderRadius: "8px",
                            margin: "4px",
                            "&:hover": {
                              backgroundColor: "#f1f5f9",
                            },
                            "&.Mui-selected": {
                              backgroundColor: "#5652B0 !important",
                              color: "white",
                              "&:hover": {
                                backgroundColor: "#4A47A3 !important",
                              },
                            },
                          }}>
                          Overall Transfer
                        </MenuItem>
                        <MenuItem
                          value="colour_wise"
                          sx={{
                            borderRadius: "8px",
                            margin: "4px",
                            "&:hover": {
                              backgroundColor: "#f1f5f9",
                            },
                            "&.Mui-selected": {
                              backgroundColor: "#5652B0 !important",
                              color: "white",
                              "&:hover": {
                                backgroundColor: "#4A47A3 !important",
                              },
                            },
                          }}>
                          Colour Wise Transfer
                        </MenuItem>
                        <MenuItem
                          value="size_wise"
                          sx={{
                            borderRadius: "8px",
                            margin: "4px",
                            "&:hover": {
                              backgroundColor: "#f1f5f9",
                            },
                            "&.Mui-selected": {
                              backgroundColor: "#5652B0 !important",
                              color: "white",
                              "&:hover": {
                                backgroundColor: "#4A47A3 !important",
                              },
                            },
                          }}>
                          Size Wise Transfer
                        </MenuItem>
                      </Select>
                    </FormControl>
                  )}
                </div>
                {isDesignSectionCollapsed && (
                  <span className="text-sm text-gray-500">
                    {transferDesigns.length > 0 ? (
                      <>
                        :{" "}
                        {transferDesigns.map(
                          (design, index) =>
                            design.name +
                            (index < transferDesigns.length - 1 ? ", " : ""),
                        )}
                      </>
                    ) : (
                      ": No designs added yet"
                    )}
                  </span>
                )}
              </div>
              {!isDesignSectionCollapsed && (
                <div className="flex items-center gap-4">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={transferAllDesigns}
                        onChange={e => setTransferAllDesigns(e.target.checked)}
                        sx={{
                          color: "#5652B0",
                          "&.Mui-checked": {
                            color: "#5652B0",
                          },
                        }}
                      />
                    }
                    label={
                      <span className="text-sm font-medium text-gray-700">
                        Transfer All
                      </span>
                    }
                    onClick={e => e.stopPropagation()}
                  />
                  <div className="h-6 w-px bg-gray-200" />{" "}
                  {/* Vertical separator */}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={designPaymentDue}
                        onChange={e => setDesignPaymentDue(e.target.checked)}
                        sx={{
                          color: "#5652B0",
                          "&.Mui-checked": {
                            color: "#5652B0",
                          },
                        }}
                      />
                    }
                    label={
                      <span className="text-sm font-medium text-gray-700">
                        Payment Due
                      </span>
                    }
                    onClick={e => e.stopPropagation()}
                  />
                </div>
              )}
            </div>

            <div
              className={`space-y-4 ${
                isDesignSectionCollapsed ? "hidden" : "mt-4"
              }`}>
              {transferDesigns.map(design => (
                <div key={design.id} className="space-y-4">
                  {design.pieces.map((piece, pieceIndex) => (
                    <div
                      key={pieceIndex}
                      className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                      <div className="p-4 border-b border-gray-100 bg-gray-50/50">
                        <div className="flex items-start gap-4">
                          <img
                            src={design.image || "/placeholder-design.png"}
                            alt={design.name}
                            className="w-16 h-16 object-cover rounded-lg bg-gray-100"
                          />
                          <div className="flex-1">
                            <div className="flex items-start justify-between">
                              <div className="flex items-start flex-col">
                                <div className="flex items-center gap-4">
                                  <h3 className="text-lg font-medium text-gray-800">
                                    {design.name} ({piece.pieceType})
                                  </h3>
                                  <span className="text-sm text-gray-600">
                                    {transferDesignQuantities[
                                      `${design.id}-${piece.pieceType}-default`
                                    ] || 0}
                                    /{piece.totalQty || 0} pieces
                                  </span>
                                  <TextField
                                    size="small"
                                    type="number"
                                    label="Transfer Quantity"
                                    className="min-w-[150px]"
                                    disabled={designTransferMode !== "overall"}
                                    value={
                                      transferDesignQuantities[
                                        `${design.id}-${piece.pieceType}-default`
                                      ] || ""
                                    }
                                    onChange={e => {
                                      if (designTransferMode === "overall") {
                                        const value =
                                          parseInt(e.target.value) || 0;
                                        setTransferDesignQuantities(prev => ({
                                          ...prev,
                                          [`${design.id}-${piece.pieceType}-default`]:
                                            value,
                                        }));
                                      }
                                    }}
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          pieces
                                        </InputAdornment>
                                      ),
                                    }}
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        borderRadius: "8px",
                                        backgroundColor:
                                          designTransferMode !== "overall"
                                            ? "#f3f4f6"
                                            : "transparent",
                                        "& fieldset": {
                                          borderColor:
                                            designTransferMode !== "overall"
                                              ? "#d1d5db"
                                              : "#e5e7eb",
                                        },
                                        "&:hover fieldset": {
                                          borderColor:
                                            designTransferMode !== "overall"
                                              ? "#d1d5db"
                                              : "#9ca3af",
                                        },
                                        "& input": {
                                          color:
                                            designTransferMode !== "overall"
                                              ? "#9ca3af"
                                              : "inherit",
                                        },
                                      },
                                      "& .MuiInputLabel-root": {
                                        color:
                                          designTransferMode !== "overall"
                                            ? "#9ca3af"
                                            : "#6b7280",
                                        fontWeight: 300,
                                      },
                                    }}
                                  />
                                </div>
                                <p className="text-sm text-gray-500 mt-0.5">
                                  Design ID: {design.designId}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="p-4">
                        <div className="space-y-4">
                          {designTransferMode !== "overall" &&
                            piece.colors.map((color, colorIndex) => (
                              <div key={colorIndex}>
                                <div className="w-full mb-2 flex items-center gap-4 ml-3">
                                  <span className="text-sm text-left font-medium text-gray-700">
                                    Color : {color.name}
                                  </span>
                                  <span className="text-sm text-gray-600">
                                    {transferDesignQuantities[
                                      `${design.id}-${piece.pieceType}-${color.name}-total`
                                    ] || 0}
                                    /{color.totalQty || 0} pieces
                                  </span>
                                  <TextField
                                    size="small"
                                    type="number"
                                    label="Transfer Quantity"
                                    className="min-w-[150px]"
                                    disabled={
                                      designTransferMode === "size_wise"
                                    }
                                    value={
                                      transferDesignQuantities[
                                        `${design.id}-${piece.pieceType}-${color.name}-total`
                                      ] || ""
                                    }
                                    onChange={e => {
                                      if (
                                        designTransferMode === "colour_wise"
                                      ) {
                                        const value =
                                          parseInt(e.target.value) || 0;
                                        setTransferDesignQuantities(prev => ({
                                          ...prev,
                                          [`${design.id}-${piece.pieceType}-${color.name}-total`]:
                                            value,
                                        }));
                                      }
                                    }}
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          pieces
                                        </InputAdornment>
                                      ),
                                    }}
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        borderRadius: "8px",
                                        backgroundColor:
                                          designTransferMode === "size_wise"
                                            ? "#f3f4f6"
                                            : "transparent",
                                        "& fieldset": {
                                          borderColor:
                                            designTransferMode === "size_wise"
                                              ? "#d1d5db"
                                              : "#e5e7eb",
                                        },
                                        "&:hover fieldset": {
                                          borderColor:
                                            designTransferMode === "size_wise"
                                              ? "#d1d5db"
                                              : "#9ca3af",
                                        },
                                        "& input": {
                                          color:
                                            designTransferMode === "size_wise"
                                              ? "#9ca3af"
                                              : "inherit",
                                        },
                                      },
                                      "& .MuiInputLabel-root": {
                                        color:
                                          designTransferMode === "size_wise"
                                            ? "#9ca3af"
                                            : "#6b7280",
                                        fontWeight: 300,
                                      },
                                    }}
                                  />
                                </div>
                                <div className="space-y-3">
                                  {designTransferMode === "size_wise" &&
                                    Object.entries(color.sizes).map(
                                      ([size, total]) => {
                                        const used = 0; // This would come from actual data
                                        const transferKey = `${design.id}-${piece.pieceType}-${color.name}-${size}`;
                                        return (
                                          <div
                                            key={size}
                                            className="flex items-center gap-4">
                                            <div className="min-w-[120px]">
                                              <span className="text-sm font-medium text-gray-700">
                                                {size}
                                              </span>
                                            </div>
                                            <div className="min-w-[200px]">
                                              <span className="text-sm text-gray-600">
                                                {used}/{total} pieces
                                              </span>
                                            </div>
                                            <div className="min-w-[150px]">
                                              <TextField
                                                fullWidth
                                                size="small"
                                                type="number"
                                                label="Transfer Quantity"
                                                value={
                                                  transferDesignQuantities[
                                                    transferKey
                                                  ] || ""
                                                }
                                                onChange={e => {
                                                  const value =
                                                    parseInt(e.target.value) ||
                                                    0;
                                                  setTransferDesignQuantities(
                                                    prev => ({
                                                      ...prev,
                                                      [transferKey]: value,
                                                    }),
                                                  );
                                                }}
                                                InputProps={{
                                                  endAdornment: (
                                                    <InputAdornment position="end">
                                                      pieces
                                                    </InputAdornment>
                                                  ),
                                                }}
                                                sx={{
                                                  "& .MuiOutlinedInput-root": {
                                                    borderRadius: "8px",
                                                    "& fieldset": {
                                                      borderColor: "#e5e7eb",
                                                    },
                                                    "&:hover fieldset": {
                                                      borderColor: "#d1d5db",
                                                    },
                                                  },
                                                  "& .MuiInputLabel-root": {
                                                    color: "#9ca3af",
                                                    fontWeight: 300,
                                                  },
                                                }}
                                              />
                                            </div>
                                          </div>
                                        );
                                      },
                                    )}
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={handleBack}
            className="px-6 py-2 text-gray-700 font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            Cancel
          </button>
          <button
            onClick={() => {
              // Add validation and transfer logic here
              console.log("Transfer clicked", form);
            }}
            className="px-6 py-2 bg-[#5652B0] text-white font-medium rounded-lg hover:bg-[#4A47A3] transition-colors">
            Transfer
          </button>
        </div>
      </div>

      {/* Taka Modal */}
      <Dialog
        open={showTakaModal}
        onClose={handleCloseTakaModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            padding: "0",
          },
        }}>
        <DialogTitle
          sx={{
            fontSize: "1.25rem",
            fontWeight: 600,
            color: "#1F2937",
            borderBottom: "1px solid #E5E7EB",
            padding: "20px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}>
          Add Taka
          <IconButton size="small" onClick={handleCloseTakaModal}>
            <X size={18} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ padding: "24px" }}>
          {selectedStockEntry && (
            <div className="space-y-6">
              {/* Stock Entry Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">
                  Stock Entry Details
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Color:</span>
                    <span className="ml-2 font-medium">
                      {selectedStockEntry.color}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Quantity:</span>
                    <span className="ml-2 font-medium">
                      {selectedStockEntry.quantity} {selectedStockEntry.unit}
                    </span>
                  </div>
                </div>
              </div>

              {/* Add New Taka */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-4">Add New Taka</h4>

                {/* Dynamic Taka Input Fields - Added Sets */}
                {takaInputFields[selectedStockEntry.id]?.map((field, index) => (
                  <div key={field.id} className="flex gap-4 items-start mb-4">
                    <div className="min-w-[200px]">
                      <TextField
                        fullWidth
                        label="Quantity"
                        type="number"
                        value={field.quantity}
                        size="small"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              {selectedStockEntry.unit}
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiInputBase-input": {
                            backgroundColor: "white",
                          },
                        }}
                      />
                    </div>
                    <div className="min-w-[200px]">
                      <TextField
                        fullWidth
                        label="Number of Pieces"
                        type="number"
                        value={field.value}
                        size="small"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              pieces
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          "& .MuiInputBase-input": {
                            backgroundColor: "white",
                          },
                        }}
                      />
                    </div>
                    <IconButton
                      onClick={() =>
                        handleRemoveTakaField(selectedStockEntry.id, field.id)
                      }
                      className="mt-1"
                      size="small">
                      <Trash2 size={18} className="text-red-500" />
                    </IconButton>
                  </div>
                ))}

                {/* Initial Input Fields - At Bottom */}
                <div className="flex gap-4 items-start mb-4">
                  <div className="min-w-[200px]">
                    <TextField
                      fullWidth
                      label="Quantity"
                      type="number"
                      value={newTakaQuantity}
                      onChange={e => setNewTakaQuantity(e.target.value)}
                      size="small"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {selectedStockEntry.unit}
                          </InputAdornment>
                        ),
                      }}
                    />
                  </div>
                  <div className="min-w-[200px]">
                    <TextField
                      fullWidth
                      label="Number of Pieces"
                      type="number"
                      value={newTakaValue}
                      onChange={e => setNewTakaValue(e.target.value)}
                      size="small"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">pieces</InputAdornment>
                        ),
                      }}
                    />
                  </div>
                  <button
                    onClick={handleAddTakaDenomination}
                    disabled={!newTakaQuantity || !newTakaValue}
                    className="px-4 py-2 bg-[#5652B0] text-white rounded-lg text-sm font-medium hover:bg-[#4A47A3] disabled:opacity-50 disabled:cursor-not-allowed">
                    Add Taka
                  </button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            padding: "16px 24px 24px",
            justifyContent: "flex-end",
          }}>
          <Button
            onClick={handleCloseTakaModal}
            sx={{
              color: "rgba(0, 0, 0, 0.6)",
              textTransform: "none",
              fontWeight: 500,
              borderRadius: "8px",
              padding: "8px 16px",
            }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default TransferJobslip;
